"use client";
import { useState, useEffect, useCallback } from "react";
import { Input, Button, Tooltip } from "@nextui-org/react";
import {
  IoAddCircleOutline,
  IoRemoveCircleOutline,
  IoInformationCircleOutline,
  IoCloseCircleOutline,
} from "react-icons/io5";
import { checkExistingIdentifier, isValidIdentifier } from "../actions/author";

const DynamicIdentifierList = ({
  authorId,
  label,
  identifiers,
  onChange,
  disabled,
  setHasError,
}) => {
  const dict = {
    ORCID: "orcid",
    "Scopus ID": "scopus",
    "Google Scholar ID": "google",
  };

  const [items, setItems] = useState(
    identifiers.length > 0 ? identifiers : [""]
  );
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [validationMessages, setValidationMessages] = useState({});

  const extractIdentifier = (value, type) => {
    try {
      const url = new URL(value);
      if (type === "ORCID" && url.hostname === "orcid.org") {
        return url.pathname.slice(1);
      }
      if (type === "Scopus ID" && url.hostname === "www.scopus.com") {
        const params = new URLSearchParams(url.search);
        return params.get("authorId") || "";
      }
      if (
        type === "Google Scholar ID" &&
        url.hostname.startsWith("scholar.google.")
      ) {
        const params = new URLSearchParams(url.search);
        return params.get("user") || "";
      }
    } catch {
      // not a valid URL, return as is
    }
    return value;
  };

  const handleAddItem = useCallback(() => {
    if (items[items.length - 1].trim() !== "") {
      const newItems = [...items, ""];
      setItems(newItems);
      onChange(newItems);
    }
  }, [items, onChange]);

  useEffect(() => {
    if (items.length === 0) {
      setItems([""]);
    }
  }, [items.length]);

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      onChange(newItems);
    }
  };

  const handleCleanItem = (index) => {
    const newItems = items.map((item, i) => (i === index ? "" : item));
    setItems(newItems);
    onChange(newItems);
  };

  const handleChange = async (index, rawValue) => {
    const extractedValue = extractIdentifier(rawValue, label);
    const newItems = items.map((item, i) =>
      i === index ? extractedValue : item
    );
    setItems(newItems);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    setValidationMessages((prev) => ({
      ...prev,
      [index]: { message: "Verificando...", type: "pending" },
    }));

    const newTimeout = setTimeout(async () => {
      if (extractedValue.trim() === "") {
        setValidationMessages((prev) => ({
          ...prev,
          [index]: null,
        }));
        return;
      }

      const isValid = await isValidIdentifier(extractedValue, dict[label]);
      if (!isValid) {
        setHasError(true);
        setValidationMessages((prev) => ({
          ...prev,
          [index]: {
            message: "El identificador no es válido.",
            type: "error",
          },
        }));
        return;
      }

      const existingIdentifier = await checkExistingIdentifier(
        extractedValue,
        authorId
      );
      if (!existingIdentifier) {
        onChange(newItems);
        setValidationMessages((prev) => ({
          ...prev,
          [index]: {
            message: "Identificador sin uso.",
            type: "success",
          },
        }));
        setHasError(false);
      } else {
        setHasError(true);
        setValidationMessages((prev) => ({
          ...prev,
          [index]: {
            message: "El identificador ya está siendo usado por otro perfil.",
            type: "error",
          },
        }));
      }
    }, 1000);

    setDebounceTimeout(newTimeout);
  };

  const getTooltipContent = (type) => {
    switch (type) {
      case "ORCID":
        return "Puedes obtener tu ORCID desde https://orcid.org copiando el número al final de tu URL de perfil.";
      case "Scopus ID":
        return "Tu Scopus ID está al final del enlace en Scopus, después de 'authorId='. Ejemplo: authorId=6506281895";
      case "Google Scholar ID":
        return "El ID de Google Scholar se encuentra después de 'user=' en tu URL de perfil. Ejemplo: user=_Q1HH6gAAAAJ";
      default:
        return "Ingresa un identificador válido o el enlace para extraerlo automáticamente.";
    }
  };

  return (
    <div className="mb-4 w-full">
      <div className="flex items-center mb-2">
        <label className="block text-sm font-medium text-gray-700 mr-2">
          {label}
        </label>
        <Tooltip
          content={getTooltipContent(label)}
          placement="right"
          color="foreground"
        >
          <span className="text-gray-500 cursor-pointer">
            <IoInformationCircleOutline size={18} />
          </span>
        </Tooltip>
      </div>

      {items.map((item, index) => {
        const isFirstAndEmpty =
          index === 0 && items.length === 1 && item === "";

        return (
          <div key={index} className="mb-3">
            <div className="flex items-center space-x-2">
              <Input
                value={item}
                onChange={(e) => handleChange(index, e.target.value)}
                placeholder={`${label} ${index + 1}`}
                className="flex-grow"
                isDisabled={disabled}
              />
              {!isFirstAndEmpty && (
                <>
                  <Button
                    auto
                    isIconOnly
                    color="primary"
                    variant="faded"
                    onPress={handleAddItem}
                    startContent={<IoAddCircleOutline size={24} />}
                    isDisabled={
                      disabled ||
                      items[items.length - 1].trim() === "" ||
                      validationMessages[items.length - 1]?.type === "error"
                    }
                  />
                  {items.length === 1 ? (
                    <Button
                      auto
                      isIconOnly
                      color="danger"
                      variant="faded"
                      onPress={() => handleCleanItem(index)}
                      startContent={<IoCloseCircleOutline size={24} />}
                      isDisabled={
                        disabled || items[items.length - 1].trim() === ""
                      }
                    />
                  ) : (
                    <Button
                      auto
                      isIconOnly
                      color="danger"
                      variant="faded"
                      onPress={() => handleRemoveItem(index)}
                      startContent={<IoCloseCircleOutline size={24} />}
                      isDisabled={disabled}
                    />
                  )}
                </>
              )}
            </div>
            {validationMessages[index] && (
              <p
                className={`text-sm ml-2 ${
                  validationMessages[index].type === "error"
                    ? "text-red-500"
                    : validationMessages[index].type === "success"
                    ? "text-green-500"
                    : "text-gray-500"
                }`}
              >
                {validationMessages[index].message}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicIdentifierList;
